import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import AgentCreate from '../components/AgentCreate/AgentCreate';
import Agents from '../components/Agents/Agents';
import AgentPlayground from '../components/AgentPlayground/AgentPlayground';
import KnowledgeBase from '../components/KnowledgeBase/KnowledgeBase';
import KnowledgeBaseHub from '../components/KnowledgeBaseHub/KnowledgeBaseHub';
import FolderFilesView from '../components/KnowledgeBaseView/FolderFilesView';
import KnowledgeBaseD3 from '../components/KnowledgeBaseD3/KnowledgeBaseD3';
import FolderFilesViewD3 from '../components/KnowledgeBaseViewD3/FolderFilesViewD3';
import Models from '../components/Models/Models';
import MainContent from '../components/MainContent/MainContent';
import ToolsConfigure from '../components/ToolsConfigure/ToolsConfigure';
import OAuthCallback from '../components/ToolsConfigure/OAuthCallback';
import Login from '../components/Login/Login';
import Templates from '../components/Templates/Templates';
import ProtectedRoute from '../components/ProtectedRoute/ProtectedRoute';
import PermissionRoute from '../components/ProtectedRoute/PermissionRoute';
import Webhooks from '../components/Webhooks/Webhooks';
import Schedules from '../components/Schedules/Schedules';
import AgentTriggers from '../components/AgentTriggers/AgentTriggers';
import BatchJobs from '../components/BatchJobs/BatchJobs';
import BatchJobDetail from '../components/BatchJobs/BatchJobDetail';
import MyLLMKeys from '../components/MyLLMKeys/MyLLMKeys';
import ConnectPlatformMcp from '../components/PlatformMcp/ConnectPlatformMcp';
import AssistantUploadRedirect from '../components/Assistant/AssistantUploadRedirect';
import AssistantToolkitConnect from '../components/Assistant/AssistantToolkitConnect';
import AssistantOAuthDone from '../components/Assistant/AssistantOAuthDone';
import RunHistory from '../components/RunHistory/RunHistory';
import PlatformAdmin from '../components/PlatformAdmin/PlatformAdmin';
import CompanyAdmin from '../components/CompanyAdmin/CompanyAdmin';
import PartnerAdmin from '../components/PartnerAdmin/PartnerAdmin';
import SharedWithMe from '../components/ResourceSharing/SharedWithMe';
import MCPServers from '../components/MCPServers/MCPServers';
import McpServerDetail from '../components/MCPServers/McpServerDetail';
import KnowledgeGraph from '../components/KnowledgeGraph/KnowledgeGraph';
import KgDatasetDetail from '../components/KnowledgeGraph/KgDatasetDetail';
import { PERMISSIONS } from '../constants/permissions';

/**
 * Wrap a route element in the auth guard, plus an optional permission guard.
 * @param {React.ReactNode} element
 * @param {{ permissions?: string[], requirePlatformAdmin?: boolean, requirePartner?: boolean }} [opts]
 */
const guarded = (element, opts = {}) => {
  const { permissions, requirePlatformAdmin, requirePartner } = opts;
  const needsPermissionGuard =
    requirePlatformAdmin || requirePartner || (permissions && permissions.length > 0);
  const content = needsPermissionGuard ? (
    <PermissionRoute
      permissions={permissions}
      requirePlatformAdmin={requirePlatformAdmin}
      requirePartner={requirePartner}
    >
      {element}
    </PermissionRoute>
  ) : (
    element
  );
  return <ProtectedRoute>{content}</ProtectedRoute>;
};

const AppRoutes = () => {
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      {/* Home + OAuth callback are intentionally ungated (landing page / auth flow). */}
      <Route path="/" element={guarded(<MainContent />)} />
      <Route path="/agents" element={guarded(<Agents />, { permissions: [PERMISSIONS.AGENTS] })} />
      <Route path="/agent-create" element={guarded(<AgentCreate />, { permissions: [PERMISSIONS.AGENTS] })} />
      <Route path="/agent-test" element={guarded(<AgentPlayground />, { permissions: [PERMISSIONS.AGENTS] })} />
      <Route
        path="/configure/tools"
        element={guarded(<ToolsConfigure />, { permissions: [PERMISSIONS.COMPOSIO, PERMISSIONS.CUSTOM_TOOLS] })}
      />
      <Route
        path="/tools"
        element={<Navigate to="/configure/tools" replace />}
      />
      {/* MCP Server Builder — publish an API / agent / KB folder as a hosted
          MCP server. Detail screen keys its section off ?tab= so every step is
          deep-linkable. */}
      <Route
        path="/mcp-servers"
        element={guarded(<MCPServers />, { permissions: [PERMISSIONS.MCP_SERVERS] })}
      />
      <Route
        path="/mcp-servers/:serverId"
        element={guarded(<McpServerDetail />, { permissions: [PERMISSIONS.MCP_SERVERS] })}
      />
      {/* Knowledge Graph — faceted datasets. Despite the name this is a faceted
          search engine, not a citation network: records carry declared facets the
          agent can filter and count over EXACTLY, which vector search cannot do.
          The detail screen keys its section off ?tab= so every step is
          deep-linkable, and a dataset stays invisible to agents until it is
          activated. */}
      <Route
        path="/knowledge-graph"
        element={guarded(<KnowledgeGraph />, { permissions: [PERMISSIONS.KNOWLEDGE_GRAPH] })}
      />
      <Route
        path="/knowledge-graph/:datasetId"
        element={guarded(<KgDatasetDetail />, { permissions: [PERMISSIONS.KNOWLEDGE_GRAPH] })}
      />
      <Route path="/oauth/callback" element={guarded(<OAuthCallback />)} />
      <Route
        path="/knowledge-base"
        element={guarded(<Navigate to="/knowledge-hub?tab=d1" replace />)}
      />
      <Route
        path="/knowledge-hub"
        element={guarded(<KnowledgeBaseHub />, { permissions: [PERMISSIONS.KB_DIM1, PERMISSIONS.KB_DIM3] })}
      />
      <Route
        path="/knowledge-base/upload"
        element={guarded(<KnowledgeBase />, { permissions: [PERMISSIONS.KB_DIM1] })}
      />
      <Route
        path="/knowledge-base/folder/:folderId"
        element={guarded(<FolderFilesView />, { permissions: [PERMISSIONS.KB_DIM1] })}
      />
      <Route
        path="/knowledge-base/folder/:folderId/upload"
        element={guarded(<KnowledgeBase />, { permissions: [PERMISSIONS.KB_DIM1] })}
      />
      <Route
        path="/knowledge-base-d3"
        element={guarded(<Navigate to="/knowledge-hub?tab=d3" replace />)}
      />
      <Route
        path="/knowledge-base-d3/upload"
        element={guarded(<KnowledgeBaseD3 />, { permissions: [PERMISSIONS.KB_DIM3] })}
      />
      <Route
        path="/knowledge-base-d3/folder/:folderId"
        element={guarded(<FolderFilesViewD3 />, { permissions: [PERMISSIONS.KB_DIM3] })}
      />
      <Route
        path="/knowledge-base-d3/folder/:folderId/upload"
        element={guarded(<KnowledgeBaseD3 />, { permissions: [PERMISSIONS.KB_DIM3] })}
      />
      {/* Models is read-only for everyone (GETs are ungated); catalog edits live in
          the Platform Admin console — so the view route stays open. */}
      <Route path="/models" element={guarded(<Models />)} />
      <Route
        path="/configure/webhooks"
        element={guarded(<Webhooks />, { permissions: [PERMISSIONS.WEBHOOKS] })}
      />
      <Route
        path="/configure/triggers"
        element={guarded(<AgentTriggers />, { permissions: [PERMISSIONS.TRIGGERS] })}
      />
      {/* Schedules list/toggle/delete are available to everyone; only create/edit are
          gated (inside the screen), so the route itself stays open. */}
      <Route path="/schedules" element={guarded(<Schedules />)} />
      <Route
        path="/batch-jobs"
        element={guarded(<BatchJobs />, { permissions: [PERMISSIONS.BATCH_JOBS] })}
      />
      <Route
        path="/batch-jobs/:jobId"
        element={guarded(<BatchJobDetail />, { permissions: [PERMISSIONS.BATCH_JOBS] })}
      />
      <Route
        path="/templates"
        element={guarded(<Templates />, { permissions: [PERMISSIONS.TEMPLATES] })}
      />
      <Route
        path="/my-llm-keys"
        element={guarded(<MyLLMKeys />, { permissions: [PERMISSIONS.LLM_KEYS_USER] })}
      />
      {/* Connect Platform MCP. Auth only: the menu item needs `platform_mcp`,
          but a user who lost it must still reach My keys to revoke, and the
          page shows "not enabled" itself on a 403. */}
      <Route path="/platform-mcp" element={guarded(<ConnectPlatformMcp />)} />
      <Route path="/connect-assistant" element={<Navigate to="/platform-mcp" replace />} />
      {/* Pages Claude / Town link to (ASSISTANT_*_URL settings). Auth only:
          each checks its own permission so it can say what is missing in
          plain words, and a logged-out user returns here after login. */}
      <Route path="/assistant/upload/:kb/:folderId" element={guarded(<AssistantUploadRedirect />)} />
      <Route path="/assistant/connect/:toolkit" element={guarded(<AssistantToolkitConnect />)} />
      <Route path="/assistant/oauth-done" element={guarded(<AssistantOAuthDone />)} />
      {/* Own run history + run details — same `dashboard` key the metrics
          endpoints behind it are gated on. */}
      <Route
        path="/run-history"
        element={guarded(<RunHistory />, { permissions: [PERMISSIONS.DASHBOARD] })}
      />
      <Route path="/shared-with-me" element={guarded(<SharedWithMe />)} />
      <Route
        path="/companies"
        element={guarded(<PlatformAdmin />, { requirePlatformAdmin: true })}
      />
      <Route
        path="/company-admin"
        element={guarded(<CompanyAdmin />, {
          permissions: [
            PERMISSIONS.USER_MANAGEMENT,
            PERMISSIONS.ROLE_MANAGEMENT,
            PERMISSIONS.LLM_KEYS_COMPANY,
            PERMISSIONS.USAGE_ANALYTICS,
            PERMISSIONS.RESOURCE_SHARING,
          ],
        })}
      />
      {/* Partner admin area (Partner tier guide §5) — any partner_* key. */}
      <Route
        path="/partner-admin"
        element={guarded(<PartnerAdmin />, { requirePartner: true })}
      />
    </Routes>
  );
};

export default AppRoutes;
